import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import {
  IconChevronLeft,
  IconChevronDown,
  IconChevronRight,
  IconNotebook,
  IconPrescription,
} from "@tabler/icons-react";
import MInputField from "@/components/ui/atoms/MInputField";
import MTextArea from "@/components/ui/atoms/MTextArea";
import { MButton } from "@/components/ui/atoms/MButtons";
import MDialog from "@/components/ui/atoms/MDialog";
import MTabs from "@/components/ui/molecules/MTabs";
import MDateInputField from "@/components/ui/molecules/MDateInputField";
import MDropDownDrawer, {
  DropdownItem,
} from "@/components/ui/molecules/MDropDownDrawer";
import AutoCompleteInput from "@/components/ui/organisms/AutoCompleteInput";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { useFixMobileKeyboard } from "@/hooks/useFixMobileKeyboard";
import { getMemberInfo } from "@/hooks/useMembers";
import { usePillbox } from "@/hooks/usePillbox";
import { useMedicines } from "@/hooks/useMedicine";
import { documentService } from "@/services/documents.service";
import { Medicine } from "@/types/medicine";
import {
  CreateMedicineItemPayload,
  DayFrequency,
  DaytimeSlot,
  FoodContext,
  MedicineItemType,
  MedicineStatus,
  MedicineTimingRuleType,
  PillboxItem,
} from "@/types/pillbox";
import CategoryPicker, { MedicineCategory } from "./components/CategoryPicker";
import DaytimeMultiSelect from "./components/DaytimeMultiSelect";
import WeekdayChips from "./components/WeekdayChips";
import DuplicateMedicineModal from "./components/DuplicateMedicineModal";
import {
  deriveSlotFromTime,
  formatDisplayDate,
  formatMedicineTitle,
  getMemberLabel,
  isRelatedMedicineName,
} from "./utils/formatMedicine";
import styles from "./AddMedicine.module.css";

type RepeatsOption =
  | "EVERY_DAY"
  | "EVERY_OTHER_DAY"
  | "SPECIFIC_DAYS"
  | "EVERY_X_DAYS";

type FormMode = "add" | "edit" | "restore" | "review";

interface AddMedicineNavState {
  defaultCategory?: MedicineCategory;
  mode?: FormMode;
  item?: PillboxItem;
  pendingId?: string;
  siblingPendingIds?: string[];
  /** List/Schedule tab the landing screen was on before opening this form —
   *  set either by PillboxLanding's handleAdd ("add" mode, via its FAB) or
   *  forwarded by MedicineDetail's handleEditDetails ("edit" mode, from
   *  whichever tab the medicine was opened from) — forwarded back so
   *  returning to the landing screen restores the right tab instead of
   *  defaulting to Schedule. Not meaningful for restore/review. */
  fromView?: "schedule" | "list";
  /** Set whenever this form was opened from a specific non-landing screen
   *  that Back/Save must return to instead of the Pillbox landing screen —
   *  MedicineDetail's handleEditDetails (edit), or an "Add" FAB on
   *  SosLanding/PastMedications (add). Absent when opened directly from a
   *  List/Schedule row on the landing screen itself (PillboxLanding's own
   *  handleEditDetails/handleAdd), where there's no other screen to return
   *  to and `fromView` above is used instead. */
  returnTo?: string;
}

const DAYTIME_SLOT_TIMES: Record<DaytimeSlot, string> = {
  morning: "09:00",
  afternoon: "13:00",
  evening: "17:00",
  night: "21:00",
};

const SLOT_COUNT_TO_DAY_FREQUENCY: Record<number, DayFrequency> = {
  1: DayFrequency.ONCE,
  2: DayFrequency.TWICE,
  3: DayFrequency.THRICE,
  4: DayFrequency.FOUR_TIMES,
};

function toDateOnlyIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function deriveRepeats(item: PillboxItem): {
  repeatsOption: RepeatsOption;
  everyXDays: string;
  selectedWeekdays: number[];
} {
  if (item.selectedWeekdays && item.selectedWeekdays.length > 0) {
    return {
      repeatsOption: "SPECIFIC_DAYS",
      everyXDays: "2",
      selectedWeekdays: item.selectedWeekdays,
    };
  }
  const interval = item.recurrenceIntervalDays ?? 1;
  if (interval === 2) {
    return {
      repeatsOption: "EVERY_OTHER_DAY",
      everyXDays: "2",
      selectedWeekdays: [],
    };
  }
  if (interval > 2) {
    return {
      repeatsOption: "EVERY_X_DAYS",
      everyXDays: String(interval),
      selectedWeekdays: [],
    };
  }
  return { repeatsOption: "EVERY_DAY", everyXDays: "2", selectedWeekdays: [] };
}

// Keyed by location.key so navigating from the duplicate-check modal back
// into this same route (edit/restore mode) forces a full remount instead of
// reusing stale form state from the "add" attempt that was just abandoned.
const AddMedicine: React.FC = () => {
  const location = useLocation();
  return <AddMedicineForm key={location.key} />;
};

const AddMedicineForm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  useFixMobileKeyboard();

  // Hides the fixed "Add to Pillbox" footer while the keyboard is open, so
  // it doesn't sit on top of the field being typed into.
  const [isInputFocused, setIsInputFocused] = useState(false);
  useEffect(() => {
    // Only a field the user can actually TYPE into raises the keyboard.
    // Start/stop date, Repeats and Food context are all rendered as readOnly
    // <input>s that open a drawer on tap — counting those as "keyboard open"
    // unmounted the footer the moment a date was picked, and focus stayed on
    // the readOnly input afterwards so focusout never fired to bring it
    // back. The "Add to Pillbox" button simply vanished for the rest of the
    // session.
    const isTypingField = (el: Element | null) =>
      !!el &&
      (el.tagName === "INPUT" || el.tagName === "TEXTAREA") &&
      !(el as HTMLInputElement).readOnly &&
      !(el as HTMLInputElement).disabled;

    const handleFocusIn = (e: FocusEvent) => {
      if (isTypingField(e.target as Element)) {
        setIsInputFocused(true);
      }
    };
    const handleFocusOut = () => {
      requestAnimationFrame(() => {
        if (!isTypingField(document.activeElement)) {
          setIsInputFocused(false);
        }
      });
    };
    window.addEventListener("focusin", handleFocusIn);
    window.addEventListener("focusout", handleFocusOut);
    return () => {
      window.removeEventListener("focusin", handleFocusIn);
      window.removeEventListener("focusout", handleFocusOut);
    };
  }, []);

  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id || "";
  const memberDisplayName = memberInfo.nickname || memberInfo.firstName;
  const {
    addMedicine,
    updateItem,
    resumeItem,
    fetchLedger,
    confirmPending,
    rejectPending,
  } = usePillbox(memberId);
  const {
    searchMedicines,
    searchResults,
    searchLoading,
    searchHasMore,
    loadMoreSearchResults,
  } = useMedicines();

  // The app shares one scrollable <main> container across routes, and
  // nothing resets it on navigation — so if the landing screen was
  // scrolled down when its CTA was tapped (e.g. "Edit details" near the
  // bottom of a card), this page would otherwise open already scrolled to
  // that same leftover position instead of at its own top.
  useEffect(() => {
    document.querySelector("main")?.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const navState = location.state as AddMedicineNavState | null;
  const mode: FormMode = navState?.mode ?? "add";
  const existingItem = navState?.item ?? null;
  const pendingId = navState?.pendingId;
  const siblingPendingIds = navState?.siblingPendingIds;
  const wasSos =
    existingItem?.timingRuleType === MedicineTimingRuleType.CONDITIONAL;

  // A brand-new manual entry starts with no repeats choice pre-selected —
  // the field previously defaulted to "Every Day" even though the user
  // hadn't picked anything, which read as a decision made for them.
  const initialRepeats = existingItem
    ? deriveRepeats(existingItem)
    : {
        repeatsOption: null as RepeatsOption | null,
        everyXDays: "2",
        selectedWeekdays: [],
      };

  const initialCategory: MedicineCategory = existingItem
    ? existingItem.timingRuleType === MedicineTimingRuleType.CONDITIONAL
      ? "SOS"
      : "SCHEDULED"
    : navState?.defaultCategory ?? "SCHEDULED";

  const [category, setCategory] = useState<MedicineCategory>(initialCategory);
  const [itemType, setItemType] = useState<MedicineItemType>(
    existingItem?.itemType ?? MedicineItemType.MEDICINE
  );
  // "review" mode (confirming an OCR-extracted medicine) prefills with the
  // full formatMedicineTitle (name + dose, e.g. "Telma 40mg") so what's
  // shown here matches the Review list. Every other mode still prefills
  // with just the base name — folding dose into this input's value there
  // would resubmit it as part of `name` on save while the original `dose`
  // field survives untouched, duplicating the dose text (e.g. "Normaxin 1
  // tablet 1 tablet").
  //
  // KNOWN RISK for review mode, not yet handled: performSubmit's payload
  // never sends a `dose` field, so confirming a review without editing this
  // input keeps the record's original separate `dose` ("40mg") alongside
  // the now-combined `name` ("Telma 40mg") — the next time
  // formatMedicineTitle renders it (list, detail, etc.) it would read
  // "Telma 40mg 40mg".
  const [name, setName] = useState(
    existingItem
      ? mode === "review"
        ? formatMedicineTitle(existingItem)
        : existingItem.name
      : ""
  );
  const [repeatsOption, setRepeatsOption] = useState<RepeatsOption | null>(
    initialRepeats.repeatsOption
  );
  const [repeatsDrawerOpen, setRepeatsDrawerOpen] = useState(false);
  const [everyXDays, setEveryXDays] = useState(initialRepeats.everyXDays);
  const [selectedWeekdays, setSelectedWeekdays] = useState<number[]>(
    initialRepeats.selectedWeekdays
  );
  // Explicit slots (edit/restore of a previously-saved medicine) win. Otherwise
  // (e.g. a freshly OCR-extracted item being reviewed) fall back to Morning
  // when the medicine is taken once a day or no daytime info was extracted
  // at all — leaving multi-dose frequencies (twice/thrice/four times a day)
  // unselected since there's no reasonable single default for those.
  const initialSlots = (() => {
    if (!existingItem) return [];
    const explicit = (existingItem.slots ?? []).map(deriveSlotFromTime);
    if (explicit.length > 0) return explicit;
    if (
      !existingItem.dayFrequency ||
      existingItem.dayFrequency === DayFrequency.ONCE
    ) {
      return ["morning" as DaytimeSlot];
    }
    return [];
  })();
  const [selectedSlots, setSelectedSlots] =
    useState<DaytimeSlot[]>(initialSlots);
  // Instructions are never prefilled from a freshly OCR-extracted item
  // ("review" mode) — only genuinely-saved data (editing or restoring a
  // medicine already in the Pillbox) should prefill them.
  const prefillSavedDetails = mode === "edit" || mode === "restore";
  const prefillDetails = mode === "edit" || mode === "review";
  // Reason for taking is the exception: it's REQUIRED for SOS (see isValid),
  // so withholding it left the submit button permanently disabled with a
  // blank required field. Prefilled in EVERY mode that has a value to show —
  // edit/restore (saved data) and review (OCR-extracted). It's an editable
  // field, so the member still confirms or corrects whatever OCR read.
  const [reasonForTaking, setReasonForTaking] = useState(
    // Truncated to the same 20 chars the field itself enforces on typing —
    // OCR can extract a long phrase, and prefilling past the limit would let
    // a value through that the user could never have entered by hand.
    (existingItem?.reasonForTaking ?? "").slice(0, 20)
  );
  // Whatever's already on the medicine — a saved date (edit) or one OCR
  // extracted from the prescription (review). Undefined when there's simply
  // nothing to show.
  const prefilledStartDate =
    prefillDetails && existingItem?.startDate
      ? new Date(existingItem.startDate)
      : undefined;
  // With nothing to prefill, an as-needed (SOS) medicine leaves the field
  // BLANK rather than pre-filling today — the date is optional, exactly like
  // the stop date, and today read as a date the family had chosen. A
  // SCHEDULED medicine still defaults, since its schedule needs a start.
  //
  // Restore is the one exception: it restarts a stopped course as of today,
  // and the API's own resume falls back to today too — so showing today
  // keeps the form honest about what will actually be saved.
  const blankStartDate = initialCategory === "SOS" && mode !== "restore";
  const [startDate, setStartDate] = useState<Date | undefined>(
    prefilledStartDate ?? (blankStartDate ? undefined : new Date())
  );
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const moreDetailsRef = useRef<HTMLButtonElement>(null);
  // Expanding "Add more details" scrolls its header up into the middle of the
  // viewport so the fields that just appeared below it are visible — the
  // section sits near the bottom of the form, so without this it opens
  // entirely off-screen and reads as doing nothing. Runs on expand only;
  // collapsing leaves the scroll position alone.
  useEffect(() => {
    if (!showMoreDetails) return;
    moreDetailsRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  }, [showMoreDetails]);
  // Unlike startDate/doctorName, endDate is never prefilled from an
  // OCR-extracted item ("review" mode) even though prefillDetails otherwise
  // covers review too — confirming "Add to Pillbox" means the member is
  // currently taking it, so any stop date the prescription happened to
  // mention shouldn't silently carry over and cut the schedule short. Only
  // a genuinely-saved item (editing one already in the Pillbox) prefills it.
  const [endDate, setEndDate] = useState<Date | undefined>(
    mode === "edit" && existingItem?.endDate
      ? new Date(existingItem.endDate)
      : undefined
  );
  const [doctorName, setDoctorName] = useState(
    prefillDetails ? existingItem?.doctorName ?? "" : ""
  );
  const [foodContext, setFoodContext] = useState<FoodContext | undefined>(
    existingItem?.foodContext ?? undefined
  );
  const [foodContextDrawerOpen, setFoodContextDrawerOpen] = useState(false);
  const [notes, setNotes] = useState(
    prefillSavedDetails ? existingItem?.notes ?? "" : ""
  );
  const [submitting, setSubmitting] = useState(false);
  const [duplicateModal, setDuplicateModal] = useState<{
    items: PillboxItem[];
    payload: CreateMedicineItemPayload;
  } | null>(null);
  const [showMoveToScheduledConfirm, setShowMoveToScheduledConfirm] =
    useState(false);
  const [openingPrescription, setOpeningPrescription] = useState(false);

  const repeatsOptions: DropdownItem[] = [
    { value: "EVERY_DAY", label: t("pillbox.repeats.everyDay") },
    { value: "EVERY_OTHER_DAY", label: t("pillbox.repeats.everyOtherDay") },
    { value: "SPECIFIC_DAYS", label: t("pillbox.repeats.specificDays") },
    { value: "EVERY_X_DAYS", label: t("pillbox.repeats.everyXDays") },
  ];

  const foodContextOptions: DropdownItem[] = [
    { value: FoodContext.BEFORE_FOOD, label: t("pillbox.foodContext.before") },
    { value: FoodContext.AFTER_FOOD, label: t("pillbox.foodContext.after") },
    { value: FoodContext.WITH_FOOD, label: t("pillbox.foodContext.with") },
  ];

  // MDatePicker defaults its max-selectable date to today when no maxDate
  // is passed — without this, start/stop dates couldn't be set in the
  // future at all. Past dates are unaffected (MDatePicker's minYear
  // defaults to 1900, already permissive).
  const maxAllowedDate = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 6);
    return d;
  }, []);

  const repeatsLabel = repeatsOption
    ? (repeatsOptions.find((o) => o.value === repeatsOption)?.label as string)
    : undefined;
  const foodContextLabel = foodContext
    ? (foodContextOptions.find((o) => o.value === foodContext)?.label as string)
    : undefined;

  // A SCHEDULED medicine with no daytime slots selected would save
  // successfully but never generate a dose-event — require at least one
  // slot so the form can't produce a medicine that silently never shows up
  // on the schedule.
  const isValid =
    name.trim().length > 0 &&
    (category !== "SCHEDULED" || selectedSlots.length > 0) &&
    (category !== "SOS" || reasonForTaking.trim().length > 0);

  const handleCategoryChange = (next: MedicineCategory) => {
    if (
      next === "SCHEDULED" &&
      category === "SOS" &&
      selectedSlots.length === 0
    ) {
      setSelectedSlots(["morning"]);
    }
    // A SCHEDULED medicine needs a concrete start date for its schedule to
    // be generated from, so switching over from SOS (where the date is
    // optional and may have been left blank) restores today's default.
    if (next === "SCHEDULED" && !startDate) {
      setStartDate(new Date());
    }
    // Switching the other way clears that default again — otherwise picking
    // Scheduled first and then SOS leaves today's date sitting in a field
    // meant to start blank. Guarded on prefilledStartDate so switching an
    // existing medicine to SOS never discards a date it genuinely has, and
    // on restore, which deliberately shows today (see blankStartDate).
    if (next === "SOS" && !prefilledStartDate && mode !== "restore") {
      setStartDate(undefined);
    }
    setCategory(next);
  };

  const getMedicineOptionLabel = (med: Medicine) => med.medicine_name;

  const renderMedicineOption = (med: Medicine) => (
    <div>
      <div className="font-medium text-gray-900">{med.medicine_name}</div>
      {med.dosage && <div className="text-xs text-gray-500">{med.dosage}</div>}
    </div>
  );

  const handleMedicineSelect = (item: any) => {
    if (typeof item === "string") {
      setName(item);
    } else {
      setName(
        item.dosage
          ? `${item.medicine_name} ${item.dosage}`
          : item.medicine_name
      );
    }
  };

  const handleSubmit = () => {
    if (!isValid || submitting) return;
    if (mode === "review" && (!existingItem || !pendingId)) return;
    if (mode === "edit" && wasSos && category === "SCHEDULED") {
      setShowMoveToScheduledConfirm(true);
      return;
    }
    performSubmit();
  };

  // Shared by the plain "add" path (0 matches) and handleAddAsNew (the
  // similar-matches picker's "none of these" escape hatch) — "add" mode
  // never shows a toast (silent by design, matching the successKey logic
  // in performSubmit below) so this only needs to save + navigate.
  const finishAdd = async (payload: CreateMedicineItemPayload) => {
    await addMedicine(itemType, payload);
    if (navState?.returnTo) {
      navigate(navState.returnTo, { state: { fromView: navState?.fromView } });
    } else if (category === "SCHEDULED") {
      navigate("/pillbox", { state: { viewMode: navState?.fromView } });
    } else {
      navigate("/pillbox/sos");
    }
  };

  // Shared by the plain "review" confirm path (0 matches) and
  // handleAddAsNew's review-mode case — confirms the OCR-derived pending
  // change as a genuinely new medicine (the family decided none of the
  // similar existing medicines were what this actually is).
  const finishReviewConfirm = async (payload: CreateMedicineItemPayload) => {
    if (!pendingId) return;
    await confirmPending(pendingId, payload);
    if (siblingPendingIds && siblingPendingIds.length > 0) {
      await Promise.allSettled(
        siblingPendingIds.map((id) => rejectPending(id))
      );
    }
    navigate("/pillbox/review");
  };

  const performSubmit = async () => {
    setSubmitting(true);
    try {
      const isScheduled = category === "SCHEDULED";
      const slots = isScheduled
        ? selectedSlots.map((s) => DAYTIME_SLOT_TIMES[s]).sort()
        : [];

      const payload: CreateMedicineItemPayload = {
        name: name.trim(),
        timingRuleType: isScheduled
          ? MedicineTimingRuleType.FIXED_SLOTS
          : MedicineTimingRuleType.CONDITIONAL,
        slots: isScheduled ? slots : undefined,
        dayFrequency: isScheduled
          ? SLOT_COUNT_TO_DAY_FREQUENCY[slots.length]
          : undefined,
        // Both fields are always sent with a concrete value (never left
        // `undefined`) whenever scheduled — `undefined` is dropped entirely
        // by JSON.stringify, so switching away from one cadence mode to
        // another would otherwise never clear the PREVIOUS mode's stale
        // field server-side (e.g. switching Specific-days -> Every X days
        // saved the new interval but left the old selectedWeekdays array in
        // place, which display logic prioritizes over recurrenceIntervalDays).
        recurrenceIntervalDays: isScheduled
          ? repeatsOption === "SPECIFIC_DAYS"
            ? 1
            : repeatsOption === "EVERY_OTHER_DAY"
            ? 2
            : repeatsOption === "EVERY_X_DAYS"
            ? Math.max(1, parseInt(everyXDays, 10) || 1)
            : 1
          : undefined,
        selectedWeekdays: isScheduled
          ? repeatsOption === "SPECIFIC_DAYS"
            ? selectedWeekdays
            : []
          : undefined,
        reasonForTaking: reasonForTaking.trim() || undefined,
        startDate: startDate ? toDateOnlyIso(startDate) : undefined,
        endDate: endDate ? toDateOnlyIso(endDate) : undefined,
        doctorName: doctorName.trim() || undefined,
        foodContext,
        notes: notes.trim() || undefined,
      };

      if (mode === "add") {
        const ledger = await fetchLedger();
        // Active and stopped (past) medicines are searched together, not
        // tiered — with fuzzy base-name matching, "Telma" can legitimately
        // match an active "Telma 40" AND a stopped "Telma 20" (e.g. the
        // member switched doses). Only searching past medicines when there
        // were zero active matches silently dropped real candidates whose
        // status happened to differ from another match's.
        const matches = ledger.filter(
          (i) =>
            i.itemType === itemType &&
            (i.status === MedicineStatus.ACTIVE ||
              i.status === MedicineStatus.STOPPED) &&
            isRelatedMedicineName(i.name, name)
        );

        // More than one existing medicine sharing this base name (e.g.
        // typing "Crocin" when "Crocin 650" and "Crocin Advance" both exist)
        // is handled by the same modal as the single-match case — it shows
        // a selectable list plus an "add as new" escape hatch instead of
        // guessing which one the user meant.
        if (matches.length > 0) {
          setDuplicateModal({ items: matches, payload });
          return;
        }
        await finishAdd(payload);
        return;
      } else if (mode === "edit" && existingItem) {
        await updateItem(existingItem.itemType, existingItem.id, payload);
      } else if (mode === "restore" && existingItem) {
        await resumeItem(existingItem.itemType, existingItem.id, payload);
      } else if (mode === "review" && pendingId) {
        // OCR-derived suggestions go through the same check as manual adds
        // — the backend's own matching (which decides ADD vs DOSE_CHANGE
        // during reconciliation) can miss the same bare-number-dose cases
        // the client-side check was built to catch, so this is the last
        // line of defense against creating an actual duplicate ledger entry.
        const ledger = await fetchLedger();
        const matches = ledger.filter(
          (i) =>
            i.itemType === itemType &&
            (i.status === MedicineStatus.ACTIVE ||
              i.status === MedicineStatus.STOPPED) &&
            isRelatedMedicineName(i.name, name)
        );
        if (matches.length > 0) {
          setDuplicateModal({ items: matches, payload });
          return;
        }
        await finishReviewConfirm(payload);
        return;
      }

      // "add" and "review" both return early above (see finishAdd /
      // finishReviewConfirm) — only "edit" and "restore" reach here.
      // Only Restore and an actual SOS<->Scheduled category change get a
      // toast — plain edit confirmations are silent by design.
      const categoryChanged = mode === "edit" && wasSos === isScheduled;
      const successKey =
        mode === "restore"
          ? "pillbox.addMedicine.successMessageRestore"
          : categoryChanged
          ? isScheduled
            ? "pillbox.addMedicine.successMessage"
            : "pillbox.addMedicine.successMessageSos"
          : null;
      if (successKey) {
        showToast({
          variant: "success",
          message: t(successKey, {
            name: name.trim(),
            member: getMemberLabel(memberInfo, "possessive"),
          }),
        });
      }
      if (mode === "restore") {
        // Restore can be entered from more than one place (MedicineDetail's
        // kebab, the duplicate-medicine modal) — unlike add/edit, it isn't
        // tied to a specific category's landing screen, so return wherever
        // the user actually came from instead of assuming SOS/Scheduled.
        navigate(-1);
      } else if (navState?.returnTo) {
        // Opened from a specific screen (returnTo set) — saving must return
        // there too, matching the Back button above, not jump past it to
        // the landing screen's tabs. See that handler's comment for which
        // entry points this covers.
        navigate(navState.returnTo, {
          state: { fromView: navState?.fromView },
        });
      } else if (isScheduled) {
        navigate("/pillbox", { state: { viewMode: navState?.fromView } });
      } else {
        navigate("/pillbox/sos");
      }
    } catch {
      showToast({
        variant: "error",
        message: t("pillbox.addMedicine.errorMessage"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleMoveToScheduledConfirm = () => {
    setShowMoveToScheduledConfirm(false);
    performSubmit();
  };

  const handleOpenPrescription = async () => {
    if (!existingItem?.documentId || openingPrescription) return;
    setOpeningPrescription(true);
    try {
      const document = await documentService(memberId).getById(
        existingItem.documentId
      );
      navigate("/documents/view", {
        state: {
          document,
          backUrl: window.location.pathname,
          // A plain navigate(backUrl) starts a fresh history entry with no
          // state, which would drop mode/item/pendingId and silently reset
          // this page to a blank "add" form — pass the current nav state
          // back through so useBackNavigation re-attaches it and the
          // review screen renders correctly.
          backState: navState,
        },
      });
    } finally {
      setOpeningPrescription(false);
    }
  };

  const navigateToEditMatch = (item: PillboxItem) => {
    // Combined active+past matching (see performSubmit above) means the
    // items in one picker can mix statuses — derive edit-vs-restore from
    // the specific item picked, not a single mode for the whole list.
    navigate("/pillbox/add-medicine", {
      replace: true,
      state: {
        mode: item.status === MedicineStatus.ACTIVE ? "edit" : "restore",
        item,
      } as AddMedicineNavState,
    });
  };

  const handleDuplicateConfirm = async (item: PillboxItem) => {
    if (mode === "review" && pendingId) {
      // The OCR-derived suggestion turned out to match an existing medicine
      // — discard it (and any sibling prescription-version options for the
      // same group) instead of creating a duplicate ledger entry, then go
      // edit/restore the real existing item.
      setSubmitting(true);
      try {
        await rejectPending(pendingId);
        if (siblingPendingIds && siblingPendingIds.length > 0) {
          await Promise.allSettled(
            siblingPendingIds.map((id) => rejectPending(id))
          );
        }
      } catch {
        showToast({
          variant: "error",
          message: t("pillbox.addMedicine.errorMessage"),
        });
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
    }
    navigateToEditMatch(item);
  };

  const handleAddAsNew = async () => {
    if (!duplicateModal) return;
    const { payload } = duplicateModal;
    setDuplicateModal(null);
    setSubmitting(true);
    try {
      if (mode === "review") {
        await finishReviewConfirm(payload);
      } else {
        await finishAdd(payload);
      }
    } catch {
      showToast({
        variant: "error",
        message: t("pillbox.addMedicine.errorMessage"),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.headerCard}>
        <div className={styles.topBar}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => {
              // "review" mode is only ever opened from the Review list
              // (ReviewMedications' handleReview) — cancelling out of it
              // must return there, matching what a successful save already
              // does (see the mode === "review" branch in performSubmit),
              // not fall through to the Pillbox landing screen and strand
              // the user away from the rest of the pending medicines.
              if (mode === "review") {
                navigate("/pillbox/review");
                return;
              }
              // Opened from a specific screen that isn't the Pillbox landing
              // (MedicineDetail's edit/add-reminder, or an "Add" FAB on
              // SosLanding/PastMedications) — return there instead of
              // falling through to the landing screen. Checked regardless
              // of mode: MedicineDetail's edit sets this, but so does a
              // brand-new "add" opened from SOS/Past Medications, which
              // otherwise has no other signal distinguishing it from a
              // plain landing-screen add.
              if (navState?.returnTo) {
                navigate(navState.returnTo, {
                  state: { fromView: navState?.fromView },
                });
                return;
              }
              // A SOS-category edit (e.g. from SosLanding.handleEditDetails)
              // has no List/Schedule tab to return to — matches performSubmit's
              // own isScheduled branch below, which sends a saved SOS edit to
              // /pillbox/sos rather than the landing screen's tabs.
              if (mode === "edit" && category === "SOS") {
                navigate("/pillbox/sos");
                return;
              }
              // Every other mode returns to the Pillbox landing screen —
              // `fromView` restores the List/Schedule tab it was on, if any
              // (see AddMedicineNavState.fromView for where it's set).
              navigate("/pillbox", {
                state: { viewMode: navState?.fromView },
              });
            }}
          >
            <IconChevronLeft size={16} />
            {t("common.back")}
          </button>
          <h1 className={styles.title}>
            {t(
              mode === "edit"
                ? "pillbox.addMedicine.editTitle"
                : mode === "restore"
                ? "pillbox.addMedicine.restoreTitle"
                : mode === "review"
                ? "pillbox.addMedicine.reviewTitle"
                : "pillbox.addMedicine.title"
            )}
          </h1>
          <div className={styles.iconSpacer} />
        </div>
        <p className={styles.subtitle}>
          {t(
            mode === "edit"
              ? "pillbox.addMedicine.editSubtitle"
              : mode === "restore"
              ? "pillbox.addMedicine.restoreSubtitle"
              : mode === "review"
              ? "pillbox.addMedicine.reviewSubtitle"
              : "pillbox.addMedicine.subtitle",
            { member: memberDisplayName }
          )}
        </p>
      </div>

      <div className={styles.body}>
        {mode === "review" && (
          <button
            type="button"
            className={styles.prescriptionCard}
            onClick={
              existingItem?.documentId ? handleOpenPrescription : undefined
            }
            disabled={openingPrescription}
          >
            <span className={styles.prescriptionIcon}>
              <IconPrescription size={24} stroke={1.5} />
            </span>
            <span className={styles.prescriptionText}>
              <span className={styles.prescriptionLabel}>
                {/* OCR names a prescription "Dr <Name> · MMM YYYY", which is
                    what the doctor/date line below already says — so the name
                    is only used as the label when there's no doctor to show
                    (a clinic-named or renamed document). Otherwise keep the
                    generic "<member>'s prescription". */}
                {(!existingItem?.doctorName &&
                  existingItem?.prescriptionName) ||
                  t("pillbox.addMedicine.prescriptionCardLabel", {
                    member: memberDisplayName,
                  })}
              </span>
              <span className={styles.prescriptionDoctorDate}>
                {[
                  existingItem?.doctorName,
                  existingItem?.dateOfVisit
                    ? formatDisplayDate(existingItem.dateOfVisit)
                    : undefined,
                ]
                  .filter(Boolean)
                  .join(" ")}
              </span>
            </span>
            <IconChevronRight size={24} />
          </button>
        )}

        <div className={styles.field}>
          <span className={styles.smallLabel}>
            {t("pillbox.addMedicine.selectCategory")}
          </span>
          <CategoryPicker value={category} onChange={handleCategoryChange} />
        </div>

        <div className={styles.field}>
          <span className={styles.smallLabel}>
            {t("pillbox.addMedicine.selectType")}
          </span>
          <MTabs
            activeTab={itemType}
            onTabChange={(v) => setItemType(v as MedicineItemType)}
            disabled={mode === "edit" || mode === "restore"}
            variant="pill"
            tabs={[
              {
                value: MedicineItemType.MEDICINE,
                label: t("pillbox.addMedicine.medicine"),
              },
              {
                value: MedicineItemType.SUPPLEMENT,
                label: t("pillbox.addMedicine.supplement"),
              },
            ]}
          />
        </div>

        <AutoCompleteInput
          label={t("pillbox.addMedicine.nameLabel")}
          labelClassName={styles.fieldLabel}
          // Required in every category — see isValid.
          showAsterisk
          placeholder={t("pillbox.addMedicine.namePlaceholder")}
          data={searchResults}
          getOptionLabel={getMedicineOptionLabel}
          renderOption={renderMedicineOption}
          onSelect={handleMedicineSelect}
          value={name}
          onChange={setName}
          loading={searchLoading}
          onSearch={searchMedicines}
          minLength={3}
          onLoadMore={loadMoreSearchResults}
          hasMore={searchHasMore}
          variant="Outlined"
        />
        {category === "SCHEDULED" && (
          <>
            <MInputField
              label={t("pillbox.addMedicine.howOftenLabel")}
              labelClassName={styles.fieldLabel}
              placeholder={t("pillbox.addMedicine.repeatsPlaceholder")}
              value={repeatsLabel}
              onClick={() => setRepeatsDrawerOpen(true)}
              readOnly
              size="md"
              variant="Outlined"
              icon={<IconChevronDown size={16} />}
              showIcon
              onIconClick={() => setRepeatsDrawerOpen(true)}
            />
            {repeatsOption === "EVERY_X_DAYS" && (
              <MInputField
                label={t("pillbox.addMedicine.everyXDaysLabel")}
                labelClassName={styles.fieldLabel}
                value={everyXDays}
                onChange={(e) =>
                  setEveryXDays(e.target.value.replace(/\D/g, ""))
                }
                type="number"
                size="md"
                variant="Outlined"
              />
            )}
            {repeatsOption === "SPECIFIC_DAYS" && (
              <WeekdayChips
                selected={selectedWeekdays}
                onChange={setSelectedWeekdays}
              />
            )}

            <div className={styles.field}>
              <span className={styles.label}>
                {t("pillbox.addMedicine.timesADay")}
                {/* At least one slot is required for SCHEDULED — see isValid.
                    This block only renders in that category. */}
                <span className={styles.requiredAsterisk}>*</span>
              </span>
              <DaytimeMultiSelect
                selected={selectedSlots}
                onChange={setSelectedSlots}
              />
            </div>
          </>
        )}

        <MInputField
          label={t(
            category === "SOS"
              ? "pillbox.addMedicine.reasonLabelRequired"
              : "pillbox.addMedicine.reasonLabel"
          )}
          labelClassName={styles.fieldLabel}
          // Required for SOS only — the label itself already drops its
          // "(Optional)" suffix in that case; the asterisk makes it explicit
          // why the Add button stays disabled.
          showAsterisk={category === "SOS"}
          placeholder={t("pillbox.addMedicine.reasonPlaceholder")}
          value={reasonForTaking}
          onChange={(e) => setReasonForTaking(e.target.value.slice(0, 20))}
          maxLength={20}
          size="md"
          variant="Outlined"
        />

        <MDateInputField
          label={t(
            category === "SOS"
              ? "pillbox.addMedicine.startDateOptional"
              : "pillbox.addMedicine.startDate"
          )}
          labelClassName={styles.fieldLabel}
          value={startDate}
          onDateChange={setStartDate}
          // Clearable only for SOS, matching the "optional" label above: a
          // SCHEDULED medicine needs a concrete start date to generate its
          // schedule from (see the category switch), so blanking it there
          // would leave the medicine in an unschedulable state.
          onClear={
            category === "SOS" ? () => setStartDate(undefined) : undefined
          }
          datePickerTitle={t("pillbox.addMedicine.startDate")}
          maxDate={maxAllowedDate}
          size="md"
          variant="Outlined"
        />

        <button
          ref={moreDetailsRef}
          type="button"
          className={styles.moreDetailsCard}
          onClick={() => setShowMoreDetails((v) => !v)}
        >
          <span className={styles.moreDetailsIcon}>
            <IconNotebook size={24} stroke={1.5} />
          </span>
          <span className={styles.moreDetailsText}>
            <span className={styles.moreDetailsTitle}>
              {t("pillbox.addMedicine.moreDetails")}
            </span>
            <span className={styles.moreDetailsSubtitle}>
              {t("pillbox.addMedicine.moreDetailsHint")}
            </span>
          </span>
          <IconChevronDown
            size={16}
            className={`${styles.moreDetailsChevron} ${
              showMoreDetails ? styles.chevronOpen : ""
            }`}
          />
        </button>

        {showMoreDetails && (
          <>
            <MDateInputField
              label={t("pillbox.addMedicine.stopDate")}
              labelClassName={styles.fieldLabel}
              value={endDate}
              onDateChange={setEndDate}
              // Stop date is always optional, so it's always clearable.
              onClear={() => setEndDate(undefined)}
              datePickerTitle={t("pillbox.addMedicine.stopDate")}
              minDate={startDate}
              maxDate={maxAllowedDate}
              size="md"
              variant="Outlined"
            />
            <MInputField
              label={t("pillbox.addMedicine.doctorName")}
              labelClassName={styles.fieldLabel}
              placeholder={t("pillbox.addMedicine.doctorPlaceholder")}
              value={doctorName}
              onChange={(e) => setDoctorName(e.target.value)}
              size="md"
              variant="Outlined"
            />
            <MInputField
              label={t("pillbox.addMedicine.foodContext")}
              labelClassName={styles.fieldLabel}
              value={foodContextLabel}
              onClick={() => setFoodContextDrawerOpen(true)}
              readOnly
              size="md"
              variant="Outlined"
              icon={<IconChevronDown size={16} />}
              showIcon
              onIconClick={() => setFoodContextDrawerOpen(true)}
            />
            <MTextArea
              label={t("pillbox.addMedicine.instructions")}
              labelClassName={styles.fieldLabel}
              placeholder={t("pillbox.addMedicine.instructionsPlaceholder")}
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, 50))}
              maxLength={50}
            />
          </>
        )}
      </div>

      {/* Fixed footer (not sticky — see .submitButtonWrapper), hidden while
          the keyboard is open so it doesn't sit on top of the field being
          typed into. */}
      {!isInputFocused && (
        <div className={styles.submitButtonWrapper}>
          <MButton
            variant="filled"
            size="lg"
            text={t(
              mode === "edit"
                ? "pillbox.addMedicine.confirmButton"
                : mode === "restore"
                ? "pillbox.addMedicine.restoreButton"
                : "pillbox.addMedicine.submit"
            )}
            onClick={handleSubmit}
            disabled={!isValid}
            loading={submitting}
            className={styles.submitButton}
          />
        </div>
      )}

      <MDropDownDrawer
        open={repeatsDrawerOpen}
        onClose={() => setRepeatsDrawerOpen(false)}
        title={t("pillbox.addMedicine.repeats")}
        items={repeatsOptions}
        initialValue={repeatsOption ?? undefined}
        onSelect={(v) => setRepeatsOption(v as RepeatsOption)}
      />
      <MDropDownDrawer
        open={foodContextDrawerOpen}
        onClose={() => setFoodContextDrawerOpen(false)}
        title={t("pillbox.addMedicine.foodContext")}
        items={foodContextOptions}
        initialValue={foodContext}
        onSelect={(v) => setFoodContext(v as FoodContext)}
      />

      {duplicateModal && (
        <DuplicateMedicineModal
          open={!!duplicateModal}
          items={duplicateModal.items}
          memberDisplayName={getMemberLabel(memberInfo, "possessive")}
          onClose={() => setDuplicateModal(null)}
          onConfirm={handleDuplicateConfirm}
          onAddNew={
            duplicateModal.items.length > 1 ? handleAddAsNew : undefined
          }
          loading={submitting}
        />
      )}

      <MDialog
        isOpen={showMoveToScheduledConfirm}
        onClose={() => setShowMoveToScheduledConfirm(false)}
        title={t("pillbox.moveToScheduledModal.title")}
        description={t("pillbox.moveToScheduledModal.description")}
        secondaryCtaText={t("common.cancel")}
        onSecondaryCta={() => setShowMoveToScheduledConfirm(false)}
        primaryCtaText={t("common.confirm")}
        onPrimaryCta={handleMoveToScheduledConfirm}
        isPrimaryCtaLoading={submitting}
      />
    </div>
  );
};

export default AddMedicine;
